import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { format, addDays, startOfDay, endOfDay, setHours, setMinutes, isWeekend, parseISO, isBefore, isAfter, addHours, zonedTimeToUtc } from 'date-fns';
import { jwtDecode } from 'jwt-decode';

interface TimeSlot {
  start: Date;
  end: Date;
  selected: boolean;
  type: 'available' | 'busy';
}

const DAYS_OUT = 14

export function MentorAvailability() {
  const { sessionId, mentorId } = useParams();
  const navigate = useNavigate();
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [showBusySlots, setShowBusySlots] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const BUSINESS_HOURS = {
    start: 8,
    end: 18,
  };

  const generateAllSlots = (busySlots: { start: string; end: string }[]) => {
    const tomorrow = startOfDay(addDays(new Date(), 1));
    const twoWeeksOut = endOfDay(addDays(tomorrow, DAYS_OUT));
    const allSlots: TimeSlot[] = [];
    const busySlotsWithDates = busySlots.map(slot => ({
      start: parseISO(slot.start),
      end: parseISO(slot.end)
    }));
    
    let currentDate = tomorrow;
    
    while (isBefore(currentDate, twoWeeksOut)) {
      if (!isWeekend(currentDate)) {
        // For each weekday, check each hour between business hours
        for (let hour = BUSINESS_HOURS.start; hour < BUSINESS_HOURS.end; hour++) {
          const slotStart = setMinutes(setHours(currentDate, hour), 0);
          const slotEnd = addHours(slotStart, 1);
          
          // Check if this slot overlaps with any busy slots
          const isSlotAvailable = !busySlots.some(busySlot => {
            const busyStart = parseISO(busySlot.start);
            const busyEnd = parseISO(busySlot.end);
            return (
              (isBefore(slotStart, busyEnd) && isAfter(slotEnd, busyStart)) ||
              (isBefore(busyStart, slotEnd) && isAfter(busyEnd, slotStart))
            );
          });
          
          // Add all slots, marking them as either busy or available
          allSlots.push({
            start: slotStart,
            end: slotEnd,
            selected: false,
            type: isSlotAvailable ? 'available' : 'busy'
          });
        }
      }
      currentDate = addDays(currentDate, 1);
    }

    console.log("All computed slots: ", allSlots)
    
    return allSlots;
  };

  const fetchCalendarData = async () => {
    try {
      const accessToken = sessionStorage.getItem('googleAccessToken');
      
      if (!accessToken) {
        throw new Error('No Google credentials found');
      }

      console.log("Availability Access Token: ", accessToken)
      const tomorrow = startOfDay(addDays(new Date(), 1));
      const twoWeeksOut = endOfDay(addDays(tomorrow, DAYS_OUT));
      
      const response = await fetch('https://www.googleapis.com/calendar/v3/freeBusy', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timeMin: tomorrow.toISOString(),
          timeMax: twoWeeksOut.toISOString(),
          items: [{ id: 'primary' }],
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
      });

      if (!response.ok) {
        throw new Error(`Calendar API error: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.calendars?.primary?.busy) {
        const busySlots = data.calendars.primary.busy;
        console.log('Busy slots:', busySlots);
        const allSlots = generateAllSlots(busySlots);
        setSlots(allSlots);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setError('Failed to fetch calendar data. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    const accessToken = sessionStorage.getItem('googleAccessToken');
    if (accessToken) {
      fetchCalendarData();
    } else {
      setError('No Google credentials found. Please reconnect your calendar.');
      setLoading(false);
      // Redirect back to calendar connect page after a short delay
      setTimeout(() => {
        navigate(`/find-time/${sessionId}/mentor/${mentorId}/connect`);
      }, 3000);
    }
  }, [sessionId, mentorId, navigate]);

  const toggleSlot = (index: number) => {
    const slot = slots[index];
    if (slot && slot.type === 'available') {
      setSlots(currentSlots => 
        currentSlots.map((slot, i) => 
          i === index ? { ...slot, selected: !slot.selected } : slot
        )
      );
    }
  };

  const handleSubmit = () => {
    const selectedSlots = slots.filter(slot => slot.selected && slot.type === 'available');
    console.log('Selected slots:', selectedSlots);
  };

  // Group slots by date for better organization
  const displaySlots = slots.filter(slot => showBusySlots ? slot.type === 'busy' : slot.type === 'available');
  const slotsByDate = displaySlots.reduce<Record<string, TimeSlot[]>>((acc, slot) => {
    const date = format(slot.start, 'yyyy-MM-dd');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(slot);
    return acc;
  }, {});

  if (slotsByDate ) {
    console.log("Slots by date: ", slotsByDate)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Your Calendar {showBusySlots ? 'Busy Times' : 'Availability'}</h1>
          <button
            onClick={() => setShowBusySlots(!showBusySlots)}
            className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            Show {showBusySlots ? 'Available' : 'Busy'} Times
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffcc32] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your calendar availability...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : displaySlots.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No {showBusySlots ? 'busy' : 'available'} time slots found.</p>
            <p className="text-sm text-gray-500">Try selecting a different time range or checking your calendar settings.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(slotsByDate).map(([date, slots]) => (
              <div key={date} className="border rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-4">
                  {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[...slots].sort((a, b) => a.start.getTime() - b.start.getTime()).map((slot, index) => (
                    <button
                      key={`${slot.start.getTime()}-${slot.type}`}
                      onClick={() => slot.type === 'available' && !showBusySlots && toggleSlot(slots.indexOf(slot))}
                      disabled={slot.type === 'busy' || showBusySlots}
                      className={`
                        flex items-center justify-between
                        px-4 py-2 rounded-lg text-sm
                        transition-colors duration-200
                        ${slot.type === 'busy' || showBusySlots ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}
                        ${slot.selected 
                          ? 'bg-[#ffcc32] text-black'
                          : slot.type === 'busy'
                            ? 'bg-red-100 text-red-700 hover:bg-red-100'
                            : 'bg-gray-50 hover:bg-gray-100 text-gray-700'}
                      `}
                    >
                      <span>{format(slot.start, 'h:mm a')}</span>
                      {slot.type === 'available' && slot.selected && <X className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!showBusySlots && displaySlots.length > 0 && (
          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSubmit}
              className="bg-[#ffcc32] text-black font-medium px-6 py-3 rounded-lg hover:bg-[#ffd65c] transition-colors"
            >
              Submit Availability
            </button>
          </div>
        )}
      </div>
    </div>
  );
}